import React, { useState } from 'react';
import styled from 'styled-components';
import { Card } from '../styles/components';

const VarietiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-auto-rows: 1fr;
  gap: 1.5rem;
  width: 100%;
  max-width: 100%;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const VarietyCard = styled.div<{ color: string }>`
  background-color: ${props => {
    switch (props.color) {
      case 'red':
        return 'var(--red-50)';
      case 'green':
        return 'var(--green-50)';
      case 'orange':
        return 'var(--orange-50)';
      case 'purple':
        return 'var(--purple-50)';
      default:
        return 'var(--orange-50)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.color) {
      case 'red':
        return 'var(--red-100)';
      case 'green':
        return 'var(--green-100)';
      case 'orange':
        return 'var(--orange-100)';
      case 'purple':
        return 'var(--purple-100)';
      default:
        return 'var(--orange-100)';
    }
  }};
  border-radius: 0.5rem;
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const VarietyTitle = styled.h5`
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--primary);
  margin-bottom: 0.5rem;
`;

const VarietyDescription = styled.p`
  color: var(--gray-600);
  margin-bottom: 0.75rem;
  line-height: 1.5;
`;

const Ingredients = styled.p`
  font-size: 0.8rem;
  color: var(--gray-500);
  margin-top: 1rem;
  font-style: italic;
`;

const Allergens = styled.p`
  font-size: 0.8rem;
  color: var(--red-500);
  font-weight: 500;
  margin-top: 0.5rem;
`;


const InfoTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 1rem;
  text-align: center;
`;

const ToggleButton = styled.button`
  background: transparent;
  border: none;
  color: var(--primary);
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.3s ease;
  margin-top: auto;

  &:hover {
    color: var(--primary-dark);
  }

  svg {
    width: 1rem;
    height: 1rem;
    transition: transform 0.3s ease;
  }
`;

const NutritionFactsContainer = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  margin-top: ${props => props.isOpen ? '1rem' : '0'};
`;

const NutritionImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CTAContainer = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

const BuyButton = styled.a`
  display: inline-block;
  padding: 0.75rem 2rem;
  background: var(--primary);
  color: white;
  border-radius: 0.375rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s;

  &:hover {
    background: var(--primary-dark);
    transform: translateY(-1px);
  }
`;

interface TofuCrumblesVarietiesProps {
  varieties: Array<{
    title: string;
    description: string;
    color: string;
    nutritionImage?: string;
    ingredients?: string;
    allergens?: string;
  }>;
}

const TofuCrumblesVarieties: React.FC<TofuCrumblesVarietiesProps> = ({ varieties }) => {
  const [openNutrition, setOpenNutrition] = useState<boolean>(false);

  const toggleNutrition = () => {
    setOpenNutrition(!openNutrition);
  };

  return (
    <div style={{ gridColumn: '1 / -1', marginTop: '2rem' }}>
      <Card>
        <InfoTitle>Our Tofu Crumbles Varieties</InfoTitle>
        <VarietiesGrid>
          {varieties.map((variety, index) => (
            <VarietyCard key={index} color={variety.color}>
              <VarietyTitle>{variety.title}</VarietyTitle>
              <VarietyDescription>{variety.description}</VarietyDescription>
              <ToggleButton onClick={toggleNutrition}>
                {openNutrition ? 'Hide All Nutrition Facts' : 'View All Nutrition Facts'}
              </ToggleButton>
              {openNutrition && variety.nutritionImage && (
                <NutritionFactsContainer isOpen={openNutrition}>
                  <NutritionImage
                    src={variety.nutritionImage}
                    alt={`${variety.title} Nutrition Facts`}
                  />
                  {variety.ingredients && <Ingredients>{variety.ingredients}</Ingredients>}
                  {variety.allergens && <Allergens>{variety.allergens}</Allergens>}
                </NutritionFactsContainer>
              )}
            </VarietyCard>
          ))}
        </VarietiesGrid>
        <CTAContainer>
          <BuyButton href="https://shop.otapdx.com" target="_blank" rel="noopener noreferrer">
            Buy Tofu Crumbles
          </BuyButton>
        </CTAContainer>
      </Card>
    </div>
  );
};

export default TofuCrumblesVarieties; 